import { DocumentField as FieldDefinition } from '@bybeam/platform-types';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import {
  GenericFormData,
  fieldUpdate,
  updateRepeated,
} from '@platform-ui-common/hooks/useForm/index.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import Color from '@platform-ui-common/utilities/Color.js';
import { TemporaryNewDocument } from '@platform-ui/@types/document.js';
import DocumentUpload from '@platform-ui/common/components/DocumentUpload/index.js';
import { FieldContext } from '@platform-ui/utilities/application/fields/index.js';
import cx from 'classnames';
import { useRef } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { FieldProps } from './types.js';

export default function DocumentField<FormData extends GenericFormData>({
  field: { key, copy },
  validation,
  answers,
  dispatch,
  errors,
  repeated,
  context,
  setValidationOverrides: _,
  ...rest
}: FieldProps<FieldDefinition, FormData>): JSX.Element {
  const { showSnackbar } = useSnackbar();
  const addDocument = async (newDocs: TemporaryNewDocument[]): Promise<void> => {
    const previousDocuments = answers[key] || [];
    if (answers[key].length + newDocs.length > 100) {
      showSnackbar(
        <span>
          <b>Document upload failed:</b> You can upload a max of 100 documents at a time. Please try
          again.
        </span>,
      );
      return;
    }

    if (repeated) {
      dispatch(
        updateRepeated(repeated.parentKey, repeated.index, key, previousDocuments.concat(newDocs)),
      );
    } else {
      dispatch(fieldUpdate(key, previousDocuments.concat(newDocs)));
    }
  };

  const removeDocument = (_: string, url: string): void => {
    if (repeated) {
      dispatch(
        updateRepeated(
          repeated.parentKey,
          repeated.index,
          key,
          answers[key].filter((answer) => answer.url !== url),
        ),
      );
    } else {
      dispatch(
        fieldUpdate(
          key,
          answers[key].filter((answer) => answer.url !== url),
        ),
      );
    }
  };

  const helperTextId = useRef(`helper-text-${uuidV4()}`).current;

  return (
    <>
      {copy?.title && <Typography variant="h3">{copy.title}</Typography>}
      {copy?.description && <Markdown variant="body">{copy.description}</Markdown>}
      <div
        className={cx(
          'flex flex-col self-left text-left gap-y-3 sm:w-4/5 md:w-2/3 lg:w-7/12',
          context === FieldContext.Base ? 'lg:items-center' : 'lg:items-start',
        )}
      >
        <DocumentUpload
          id={repeated ? `${key}-${repeated?.index}` : key}
          documents={answers[key]}
          onAddDocument={addDocument}
          onRemoveDocument={removeDocument}
          buttonText={`Upload${validation?.required ? '*' : ''}`}
          uploadedText={false}
          containerStyle="flex flex-col justify-between gap-y-4"
          hasError={!!errors[key]}
          helperTextId={helperTextId}
          {...rest}
        />
        {errors[key] && (
          <Typography variant="body" color={Color.Error} id={helperTextId}>
            {errors[key]?.toString()}
          </Typography>
        )}
      </div>
    </>
  );
}
